import { createSelector } from 'reselect';
import { CallbackSaga, createGeneralCall } from '@altermaind/core/requests-status-management';
import { IS_MOCK_ENABLED } from '@/_shared/constants/mock';
import { GenericGet } from '@/_shared/types';
import { GenericApiError } from '@/_shared/types/apiError';
import { getCurrentOrganization } from '@/core/state/selectors';
import { JobStatus } from '@/swagger/constants/anagraphics-enums';
import { API_ORGANIZATIONS_ORGANIZATIONID_EMPLOYEES_IMPORTS_JOBID_PATH_KEY } from '@/swagger/constants/anagraphics-keys';
import {
  API_GET_EMPLOYEES_IMPORTS_BY_JOB_ID,
  KEY_GET_EMPLOYEES_IMPORTS_BY_JOB_ID,
} from '../constants';
import { MOCK_RESPONSE_GET_EMPLOYEES_IMPORTS_BY_JOB_ID } from '../mocks';

type RequestEmployeesImportsByJobId = GenericGet<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_EMPLOYEES_IMPORTS_JOBID_PATH_KEY
>['request']['path'];

export type CallParamsGetEmployeesImportsByJobId = Omit<
  RequestEmployeesImportsByJobId,
  'organizationId'
> & {
  successSaga?: CallbackSaga;
  errorSaga?: CallbackSaga;
};

type CallResponseGetEmployeesImportsByJobId = GenericGet<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_EMPLOYEES_IMPORTS_JOBID_PATH_KEY
>['response']['body'];

type CallErrorGetEmployeesImportsByJobId = GenericApiError;

type CallSelectorsGetEmployeesImportsByJobId = {
  isCreated: boolean;
  inProgress: boolean;
  inError: boolean;
  isCompleted: boolean;
};

export const getEmployeesImportsByJobIdCall = createGeneralCall<
  CallParamsGetEmployeesImportsByJobId,
  CallResponseGetEmployeesImportsByJobId,
  CallErrorGetEmployeesImportsByJobId,
  CallSelectorsGetEmployeesImportsByJobId
>({
  id: KEY_GET_EMPLOYEES_IMPORTS_BY_JOB_ID,
  url:
    ({ payload: { jobId } }) =>
    state => {
      const org = getCurrentOrganization(state);
      return API_GET_EMPLOYEES_IMPORTS_BY_JOB_ID(org, jobId);
    },
  method: 'get',
  successMock: IS_MOCK_ENABLED() ? MOCK_RESPONSE_GET_EMPLOYEES_IMPORTS_BY_JOB_ID : undefined,
  selectors: {
    inError: ({ selectors }) =>
      createSelector(selectors.response, res => res?.status === JobStatus.FAILED || false),
    isCreated: ({ selectors }) =>
      createSelector(selectors.response, res => res?.status === JobStatus.CREATED || false),
    inProgress: ({ selectors }) =>
      createSelector(selectors.response, res => res?.status === JobStatus.RUNNING || false),
    isCompleted: ({ selectors }) =>
      createSelector(selectors.response, res => res?.status === JobStatus.COMPLETED || false),
  },
});
